/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import 'whatwg-fetch';
import React from 'react';
import ReactDOM from 'react-dom';
import deepForceUpdate from 'react-deep-force-update';
import qs from 'qs';
import { createPath } from 'history';
import { addLocaleData } from 'react-intl';
import { Tracker } from 'react-tracker';
// This is so bad: requiring all locale if they are not needed?
/* @intl-code-template import ${lang} from 'react-intl/locale-data/${lang}'; */
import en from 'react-intl/locale-data/en';
import fr from 'react-intl/locale-data/fr';
/* @intl-code-template-end */
import App from './components/App';
import createFetch from './createFetch';
import configureStore from './store/configureStore';
import { updateMeta } from './DOMUtils';
import history from './history';
import createApolloClient from './core/createApolloClient';
import router from './router';
import { getIntl } from './actions/intl';
import trackerListeners from './tracking/listeners';
import { getPageViewEvent } from './tracking/events';

let url = require('./core/url').default;

// if (process.env.NODE_ENV !== 'production') {
const originalConsoleError = console.error;
if (console.error === originalConsoleError) {
  console.error = (...args) => {
    if (
      args &&
      args.length &&
      typeof args[0] === 'string' &&
      args[0].indexOf('[React Intl] Missing message:') === 0
    ) {
      return;
    }
    originalConsoleError.call(console, ...args);
  };
}

const originalConsoleWarnings = console.warn;
if (console.warn === originalConsoleWarnings) {
  console.warn = (...args) => {
    if (
      args &&
      args.length &&
      typeof args[0] === 'string' &&
      (args[0].indexOf('Warning: componentWillMount') === 0 ||
        args[0].indexOf('Warning: componentWillReceiveProps') === 0 ||
        args[0].indexOf('Warning: componentWillUpdate') === 0)
    ) {
      return;
    }
    originalConsoleWarnings.call(console, ...args);
  };
}
// }

const apolloClient = createApolloClient();

/* @intl-code-template addLocaleData(${lang}); */
addLocaleData(en);
addLocaleData(fr);
/* @intl-code-template-end */

/* eslint-disable global-require */

// Universal HTTP client
const fetch = createFetch(self.fetch, {
  baseUrl: window.App.apiUrl,
});

// Initialize a new Redux store
// http://redux.js.org/docs/basics/UsageWithReact.html
const store = configureStore(window.App.state, {
  apolloClient,
  fetch,
  history,
});

const tracker = new Tracker([trackerListeners(fetch)]);

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss());
    return () => {
      removeCss.forEach(f => f());
    };
  },
  // For react-apollo
  client: apolloClient,
  store,
  storeSubscription: null,
  // Universal HTTP client
  fetch,
  // intl instance as it can be get with injectIntl
  intl: store.dispatch(getIntl()),
  url,
  trackEvent: tracker.trackEvent,
};

// Switch off the native scroll restoration behavior and handle it manually
// https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
const scrollPositionsHistory = {};
if (window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

let onRenderComplete = function initialRenderComplete(rte) {
  tracker.trackEvent(
    getPageViewEvent({
      data: Object.assign({ landing: true }, rte.trackingData || {}),
      siteId: ((rte || {}).routeParams || {}).siteId,
    }),
  );
  const elem = document.getElementById('css');
  if (elem) elem.parentNode.removeChild(elem);
  onRenderComplete = function renderComplete(route, location) {
    document.title = route.title;

    updateMeta('description', route.description);
    // Update necessary tags in <head> at runtime here, ie:
    // updateMeta('keywords', route.keywords);
    // updateCustomMeta('og:url', route.canonicalUrl);
    // updateCustomMeta('og:image', route.imageUrl);
    // updateLink('canonical', route.canonicalUrl);
    // etc.

    let scrollX = 0;
    let scrollY = 0;
    const pos = scrollPositionsHistory[location.key];
    if (pos) {
      scrollX = pos.scrollX;
      scrollY = pos.scrollY;
    } else {
      const targetHash = location.hash.substr(1);
      if (targetHash) {
        const target = document.getElementById(targetHash);
        if (target) {
          scrollY = window.pageYOffset + target.getBoundingClientRect().top;
        }
      }
    }

    // Restore the scroll position if it was saved into the state
    // or scroll to the given #hash anchor
    // or scroll to top of the page
    window.scrollTo(scrollX, scrollY);

    // Google Analytics tracking. Don't send 'pageview' event after
    // the initial rendering, as it was already sent
    tracker.trackEvent(
      getPageViewEvent({
        data: rte.trackingData || null,
        siteId: ((route || {}).routeParams || {}).siteId,
      }),
    );
    if (window.ga) {
      window.ga('send', 'pageview', createPath(location));
    }
  };
};

const container = document.getElementById('app');
let appInstance;
let currentLocation = history.location;

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset,
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;

  context.intl = store.dispatch(getIntl());
  try {
    Array.from(
      document.getElementsByClassName('top-nav-icons--loading'),
    ).forEach(elm => elm.classList.add('is-show'));

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve({
      ...context,
      path: location.pathname,
      query: qs.parse(location.search.substring(1)), // queryString.parse(location.search, { arrayFormat: 'bracket' }),
      locale: store.getState().intl.locale,
    });

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    context.routeParams = route.routeParams || {};
    context.path = location.pathname;
    context.query = qs.parse(location.search.substring(1));

    if (route.redirect) {
      if (route.redirect.startsWith('http')) {
        window.location.href = route.redirect;
      } else {
        history.replace(route.redirect);
      }
      return;
    }

    appInstance = ReactDOM.hydrate(
      <App context={context}>{route.component}</App>,
      container,
      () => onRenderComplete(route, location),
    );

    Array.from(
      document.getElementsByClassName('top-nav-icons--loading'),
    ).forEach(elm => elm.classList.remove('is-show'));
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (action && currentLocation.key === location.key) {
      window.location.reload();
    }
  }
}

let isHistoryObserved = false;
export default function main() {
  // Handle client-side navigation by using HTML5 History API
  // For more information visit https://github.com/mjackson/history#readme
  currentLocation = history.location;
  if (!isHistoryObserved) {
    isHistoryObserved = true;
    history.listen(onLocationChange);
  }
  onLocationChange(currentLocation);
}

// globally accesible entry point
window.RSK_ENTRY = main;

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  url = require('./core/url').default;
  module.hot.accept('./router', () => {
    if (appInstance) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }
  });
}



// WEBPACK FOOTER //
// ./src/client.js